import { tenantTeaserImageSize } from '@/helpers/ImageUrls'
import { camelToKebabCase } from '@/helpers/StringHelpers'
import { ticketGroupColors } from '@/helpers/TicketGroupsColors'
import { fallback } from '@/themes/fallback/theme'
import type { TenantConfig } from '@/types/TenantConfig'
import type { SecondaryButtonStyles, Theme, ThemeOptions } from '@/types/ThemeOptions'
import Color from 'color'
import cloneDeep from 'lodash/cloneDeep'
import merge from 'lodash/merge'

export const black = 'black'
export const white = 'white'
export const darkGray = '#222222'
export const mediumGray = '#555555'
export const lightGray = '#DDE0E5'
export const veryLightGray = '#f7f7f7'

export function isDarkTheme(theme) {
  if (!theme) return false
  return theme.themeMode === 'dark'
}

export default function initializeThemeVariables(tenant: TenantConfig = { hasNoConfig: true }, themeConfig?: Theme) {
  const theme = getTheme(tenant, themeConfig)
  const isDark = isDarkTheme(theme)
  const variables = getThemeVariables(theme, isDark)
  const root = document.querySelector(':root')!

  // Clear properties
  // This is needed for visual regression testing, when switching between themes we need to clear the previous theme's properties
  root.removeAttribute('style')
  root.classList.remove('dark-theme', 'light-theme')

  // TODO: Document why it has to be the root not body
  const themeClass = isDark ? 'dark-theme' : 'light-theme'
  root.classList.add(themeClass)

  // Insert the CSS Custom Properties <style> at the top of the document <head>
  // so that themes' stylesheets may override them.
  // TODO Use CSS Layers to group styles into precedence layers; reset native elements, layout, components, themes.
  for (const key in variables) {
    document.documentElement.style.setProperty(`--${key}`, variables[key])
  }
}

export function getTheme(tenant: TenantConfig, theme?: Theme): ThemeOptions {
  const tier = theme?.tier || 1
  const defaults = tier < 3 ? cloneDeep(fallback) : {}
  const result: ThemeOptions = merge(defaults, themeOptions(tenant, theme))

  const darken = (color) =>
    Color(color)
      .darken(1 / 5)
      .hex()
  const darkPrimary = darken(result.primaryColor)
  const darkSecondary = darken(result.secondaryColor)

  const groupColors = {}
  for (const [i, color] of ticketGroupColors.entries()) {
    groupColors[`ticketGroup${i + 1}Color`] = color
  }

  const { width, height } = tenantTeaserImageSize(result.teaserImageSize)

  // Direct raw options
  const options: Partial<ThemeOptions> = {
    teaserImageWidth: `${width}px`,
    teaserImageHeight: `${height}px`,

    mobileFooterHeight: '80px',
    mobileCartHeaderHeight: '72px',
    mobileCartFooterHeight: '180px',

    // Derived options
    primaryColorDarker: darkPrimary,
    secondaryColorDarker: darkSecondary,
    primaryContrastColor: getContrastTextColor(result.primaryColor),

    // Hard-coded colors
    black: 'black',
    darkGray,
    mediumGray,
    lightGray,
    veryLightGray,

    /* The value must be in milliseconds since it's also used by JS */
    modalTransitionDuration: '200ms',
  }

  return {
    ...options,
    ...groupColors,
    ...result,
    ...getSecondaryButtonStyles(result),
  }
}

function getSecondaryButtonStyles(theme: Required<ThemeOptions>): { secondaryButton: SecondaryButtonStyles } | void {
  const styles = _getSecondaryButtonStyles(theme)
  if (styles) {
    return {
      secondaryButton: styles,
    }
  }
}
function _getSecondaryButtonStyles(theme: Required<ThemeOptions>): SecondaryButtonStyles | void {
  // Secondary button styles are only used by the fallback theme, used by tier 1 and 2 themes
  if (theme.tier < 3) {
    switch (theme.secondaryButtonStyle) {
      case 'solid':
        return {
          border: `2px solid ${theme.secondaryColor}`,
          background: theme.secondaryColor,
          textColor: getContrastTextColor(theme.secondaryColor),
          borderHover: 'var(--secondary-color-darker)',
          backgroundHover: 'var(--secondary-color-darker)',
          backgroundActive: 'white',
          textColorActive: mediumGray,
        }
      case 'outline':
        return {
          border: [`2px solid ${lightGray}`, `2px solid rgba(255, 255, 255, 0.15)`],
          background: ['white', 'transparent'],
          textColor: [mediumGray, '#fff'],
          borderHover: '#bfc1ca',
          backgroundHover: ['white', 'transparent'],
          backgroundActive: ['white', 'transparent'],
          textColorActive: [mediumGray, 'white'],
        }
    }
  }
}

function getThemeVariables(theme: ThemeOptions, isDarkTheme = false): Dictionary {
  function flattenTheme(object: Dict<any>, parentKey?: string): Dict<string> {
    const result: Dict<any> = {}

    for (const [key, value] of Object.entries(object)) {
      const k = parentKey ? `${parentKey}-${camelToKebabCase(key)}` : camelToKebabCase(key)
      if (typeof value === 'string') {
        result[k] = value
      } else if (Array.isArray(value)) {
        result[k] = isDarkTheme ? value[1] : value[0]
      } else {
        Object.assign(result, flattenTheme(value, k))
      }
    }

    return result
  }

  return flattenTheme(theme)
}

function getContrastTextColor(color) {
  return Color(color).isLight() ? 'black' : 'white'
}

export function themeOptions(tenant: TenantConfig, theme?: Theme): ThemeOptions | {} {
  if (tenant.hasNoConfig || tenant.ticketureTheme) {
    // Ticketure branding.
    return {
      primaryColor: '#4694E6',
      primaryContrastColor: 'white',
      secondaryColor: '#4694E6',
      headerBackground: '#003567',
      memberLinksColor: 'white',
      memberLinksColorHover: '#4694E6',
      cartHeaderBackgroundColor: '#003567',
      footerBackgroundColor: '#003567',
      footerTextColor: 'white',
      pageBackgroundColor: '#F7F7F7',
      headerDropShadow: '0px 0px 5px 0px rgba(0, 0, 0, 0.4)',
      datepicker: {
        selectedDayTextColor: '#ffffff',
      },
    }
  } else if (theme) {
    return theme
  } else {
    return {
      // No config theme options for gen 3.1 (2020) and 3.2 (2021) themes.
      // They do not support theme colors in config.
    }
  }
}
